import { AutocompleteChangeReason, AutocompleteProps, AutocompleteValue } from "@mui/material";
import { FocusEvent, SyntheticEvent } from "react";

export const defaultGetOptionLabel = <T>(option: T): string => {
  if (option && typeof option === "object") {
    const obj = option as Record<string, unknown>;
    if (typeof obj.label === "string") return obj.label;
    if (obj.label != null) return String(obj.label);
  }

  return String(option);
};

export const findMatchingOption = <T>(
  inputText: string,
  options: readonly T[],
  getOptionLabel: (option: T) => string
): T | undefined => {
  const trimmedInput = inputText.trim();
  if (!trimmedInput) {
    return undefined;
  }

  return options.find((option) => getOptionLabel(option).trim() === trimmedInput);
};

type AutocompleteBlurContext<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = {
  options: readonly T[];
  value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  multiple?: Multiple;
  freeSolo?: FreeSolo;
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
};

const isSelectedValue = <T>(
  candidate: string | T,
  currentValue: unknown,
  getOptionLabel: (option: T) => string,
  isOptionEqualToValue: (option: T, value: T) => boolean
) => {
  if (typeof currentValue === "string") {
    return (
      currentValue.trim() ===
      (typeof candidate === "string" ? candidate.trim() : getOptionLabel(candidate).trim())
    );
  }

  if (typeof candidate === "string") {
    return getOptionLabel(currentValue as T).trim() === candidate.trim();
  }

  return isOptionEqualToValue(candidate, currentValue as T);
};

export const handleAutocompleteBlurMatch = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  event: FocusEvent<HTMLDivElement>,
  context: AutocompleteBlurContext<T, Multiple, DisableClearable, FreeSolo>,
  onChange?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>["onChange"]
): boolean => {
  const inputElement = event.target as HTMLElement;
  if (inputElement.tagName !== "INPUT") {
    return false;
  }

  const trimmedInput = (inputElement as HTMLInputElement).value.trim();
  if (!trimmedInput) {
    return false;
  }

  const getOptionLabel = context.getOptionLabel ?? defaultGetOptionLabel;
  const isEqual =
    context.isOptionEqualToValue ?? ((option: T, currentValue: T) => option === currentValue);
  const matchedOption = findMatchingOption(trimmedInput, context.options, getOptionLabel);
  const valueToSelect = matchedOption ?? (context.freeSolo ? trimmedInput : undefined);

  if (valueToSelect === undefined) {
    return false;
  }

  const changeReason = (
    matchedOption !== undefined ? "selectOption" : "createOption"
  ) as AutocompleteChangeReason;

  if (context.multiple) {
    const currentValues = (Array.isArray(context.value) ? context.value : []) as Array<
      string | T
    >;
    if (
      currentValues.some((currentValue) =>
        isSelectedValue(valueToSelect, currentValue, getOptionLabel, isEqual)
      )
    ) {
      return false;
    }

    onChange?.(
      event as unknown as SyntheticEvent,
      [...currentValues, valueToSelect] as AutocompleteValue<
        T,
        Multiple,
        DisableClearable,
        FreeSolo
      >,
      changeReason
    );
    return true;
  }

  const currentValue = context.value as string | T | null;
  if (
    currentValue !== null &&
    currentValue !== undefined &&
    isSelectedValue(valueToSelect, currentValue, getOptionLabel, isEqual)
  ) {
    return false;
  }

  onChange?.(
    event as unknown as SyntheticEvent,
    valueToSelect as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
    changeReason
  );
  return true;
};
